import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string, shape } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { DISCOVERY_PATH_PREFIX } from '../../../util/discoveryPaths';
import { propTypes } from '../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  OutsideClickHandler,
} from '../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import TopbarBrowseCategoriesMegaMenu from '../../TopbarBrowseCategoriesMegaMenu/TopbarBrowseCategoriesMegaMenu';
import { persistCurrentLocationBeforeSellerAppNavigation } from '../../../util/postSellerLoginReturnPath';
import {
  officialCreateEventUrl,
  officialLoggedInCreateEventUrl,
  officialManageEventsUrl,
  officialMyTicketsUrl,
  officialPromotixAppBaseUrl,
} from '../../../util/promotixNavUrls';

import css from './TopbarDesktop.module.css';

const KEY_CODE_ESCAPE = 27;

const TopbarDesktop = props => {
  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    location,
  } = props;
  const [mounted, setMounted] = useState(false);
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const toggleDropdown = () => {
    setIsOpenMenu(!isOpenMenu);
  };

  const handleInformationMenuKeyDown = e => {
    if (e.keyCode === KEY_CODE_ESCAPE) {
      setIsOpenMenu(false);
    }
  };

  const marketplaceName = appConfig.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const search = (
    <TopbarSearchForm
      className={css.searchLink}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={appConfig}
    />
  );

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  const notificationDotMenuItem =
    notificationCount > 0 ? <div className={css.notificationDotMenuItem} aria-hidden="true" /> : null;

  const inboxPathMatch =
    location && typeof location.pathname === 'string'
      ? location.pathname.match(/\/inbox\/(orders|sales)\b/)
      : null;
  const activeInboxTab =
    currentPage === 'InboxPage' && inboxPathMatch ? inboxPathMatch[1] : null;

  const currentPageClass = page => {
    if (currentPage === page) {
      return css.currentPage;
    }
    if (page === 'StripePayoutPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage)) {
      return css.currentPage;
    }
    return null;
  };

  const myTicketsMenu = authenticatedOnClientSide ? (
    <Menu className={css.myTicketsMenuRoot} contentPosition="left">
      <MenuLabel
        className={css.myTicketsMenuLabel}
        isOpenClassName={css.myTicketsMenuLabelIsOpen}
      >
        <span className={css.inbox}>
          <FormattedMessage id="TopbarDesktop.myTicketsLink" />
          {notificationDot}
        </span>
      </MenuLabel>
      <MenuContent className={css.myTicketsMenuContent}>
        <MenuItem key="promotixEventTickets">
          <a className={css.myTicketsMenuItemLink} href={officialMyTicketsUrl()}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.myTicketsDropdownPromotix" />
          </a>
        </MenuItem>
        <MenuItem key="marketplaceTickets">
          <NamedLink
            className={classNames(
              css.myTicketsMenuItemLink,
              activeInboxTab === 'orders' ? css.currentPage : null
            )}
            name="InboxPage"
            params={{ tab: 'orders' }}
          >
            <span className={css.menuItemBorder} />
            <span className={css.myTicketsMenuItemRow}>
              <FormattedMessage id="TopbarDesktop.myTicketsDropdownMarketplace" />
              {notificationDotMenuItem}
            </span>
          </NamedLink>
        </MenuItem>
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.myTicketsMenuItemLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.myTicketsDropdownMyMarketplaceListings" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="RewardsPage">
          <NamedLink
            className={classNames(css.myTicketsMenuItemLink, currentPageClass('RewardsPage'))}
            name="RewardsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="Topbar.avatarMenuCashBackRewards" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="marketplaceInbox">
          <NamedLink
            className={classNames(
              css.myTicketsMenuItemLink,
              activeInboxTab === 'sales' ? css.currentPage : null
            )}
            name="InboxPage"
            params={{ tab: 'sales' }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.myTicketsDropdownInbox" />
          </NamedLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const myEventsMenu = authenticatedOnClientSide ? (
    <Menu className={css.myTicketsMenuRoot} contentPosition="left">
      <MenuLabel className={css.createListingLink} isOpenClassName={css.profileMenuIsOpen}>
        <span className={css.createListing}>
          <FormattedMessage id="TopbarDesktop.myEventsLink" />
        </span>
      </MenuLabel>
      <MenuContent className={css.myTicketsMenuContent}>
        <MenuItem key="manageEvents">
          <a className={css.myTicketsMenuItemLink} href={officialManageEventsUrl()}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.myEventsManageEvents" />
          </a>
        </MenuItem>
        <MenuItem key="createEventApp">
          <a className={css.myTicketsMenuItemLink} href={officialLoggedInCreateEventUrl()}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.myEventsCreateEvent" />
          </a>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : (
    <a className={css.createListingLink} href={officialCreateEventUrl()}>
      <span className={css.createListing}>
        <FormattedMessage id="TopbarMobileMenu.createEventLink" />
      </span>
    </a>
  );

  const profileMenu = authenticatedOnClientSide ? (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="Topbar.avatarMenuMarketplaceProfile" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="StripePayoutPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('StripePayoutPage'))}
            name="StripePayoutPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="Topbar.avatarMenuMarketplacePayout" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="PromoTixSettingsExternal">
          <a
            className={css.yourListingsLink}
            href={`${officialPromotixAppBaseUrl()}/user/account-setting`}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="Topbar.avatarMenuPromotixAccount" />
          </a>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const sellerSignupUrl = appConfig.sellerSignupUrl || `${DISCOVERY_PATH_PREFIX}/signup`;
  const sellerLoginUrl = appConfig.sellerLoginUrl || `${DISCOVERY_PATH_PREFIX}/login`;

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <a
      href={sellerSignupUrl}
      className={css.signupLink}
      onClick={() => persistCurrentLocationBeforeSellerAppNavigation(sellerSignupUrl)}
    >
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </a>
  );


  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <a
      href={sellerLoginUrl}
      className={css.loginLink}
      onClick={() => persistCurrentLocationBeforeSellerAppNavigation(sellerLoginUrl)}
    >
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </a>
  );

  return (
    <nav className={classes}>
      <div className={css.leftSideBar}>
        <LinkedLogo
          className={css.logoLink}
          format="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        />
        {search}
        <TopbarBrowseCategoriesMegaMenu />
        <OutsideClickHandler rootClassName={css.menuBox} onOutsideClick={() => setIsOpenMenu(false)}>
          <span className={css.menuTrigger} onClick={toggleDropdown}>
            <svg width="16" height="10" viewBox="18 19 18 12" xmlns="http://www.w3.org/2000/svg">
              <g fill="#34495E" fill-rule="evenodd" transform="translate(18 19)">
                <rect width="18" height="2" rx="1"></rect>
                <rect y="5" width="18" height="2" rx="1"></rect>
                <rect y="10" width="18" height="2" rx="1"></rect>
              </g>
            </svg>
            <span className={css.menuDropdown}>Information</span>
          </span>
          {isOpenMenu ? (
              <ul className={css.dropdownContent} onKeyDown={handleInformationMenuKeyDown}>
                <NamedLink className={css.editLinkDesktop} name="AboutPage">
                  <li>
                    <a ><FormattedMessage id="TopbarDesktopMenu.about" /></a>
                  </li>
                </NamedLink>
                <a className={css.editLinkDesktop} href={officialCreateEventUrl()}>
                  <li>
                    <a><FormattedMessage id="TopbarDesktopMenu.howitworkeventcreators" /></a>
                  </li>
                </a>
                <NamedLink className={css.editLinkDesktop} name="HowItWorkPage">
                  <li>
                    <a ><FormattedMessage id="TopbarDesktopMenu.howitwork" /></a>
                  </li>
                </NamedLink>
                <NamedLink className={css.editLinkDesktop} name="HowItWorkPageBuyers">
                  <li>
                    <a ><FormattedMessage id="TopbarDesktopMenu.howitworkbuyers" /></a>
                  </li>
                </NamedLink>
                <a
                  className={css.editLinkDesktop}
                  href="https://www.promotix.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <li>
                    <a><FormattedMessage id="TopbarDesktopMenu.contactsupport" /></a>
                  </li>
                </a>
              </ul>
          ) : null}
        </OutsideClickHandler>
      </div>
      <div className={css.rightSideBar}>
        {myEventsMenu}
        <NamedLink className={css.createListingLink} name="NewListingPage">
          <span className={css.createListing}>
            <FormattedMessage id="TopbarDesktop.createListing" />
          </span>
        </NamedLink>
        {myTicketsMenu}
        {profileMenu}
        {signupLink}
        {loginLink}
      </div>
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  appConfig: null,
  location: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
  location: shape({
    pathname: string,
  }),
};

export default TopbarDesktop;
